/**
 * Browser-side stash for the pre-auth landing discovery session.
 *
 * Anonymous transcripts are deliberately never persisted server-side (no user
 * → no content-encryption DEK to seal under), so the conversation lives here:
 * sessionStorage survives reloads AND the same-tab Auth0 signup round-trip,
 * which is exactly the claim flow's path. The trade-off is explicit — signing
 * up in a different tab/browser loses the chat and falls back to the classic
 * post-auth onboarding.
 *
 * Client-only module (sessionStorage); import from client components.
 */

import type { Handoff } from "./discovery";

const KEY = "sq_preauth_onboarding";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface PreauthStashMessage {
  role: "user" | "assistant";
  text: string;
}

export interface PreauthStash {
  sessionId: string;
  botName: string;
  chosenType: string;
  messages: PreauthStashMessage[];
  userTurns: number;
  suggestion: Handoff | null;
  /** The languageHint the session was opened with. A NOT-yet-engaged stash
   *  (zero user turns) is discarded when this no longer matches — switching
   *  the site language regenerates the opener in the new language. Absent on
   *  pre-field stashes (treated as a mismatch, which only costs a fresh
   *  opener for an unengaged chat). */
  language?: string;
  updatedAt: number;
}

const isMessage = (value: unknown): value is PreauthStashMessage => {
  if (typeof value !== "object" || value === null) return false;
  const m = value as Partial<PreauthStashMessage>;
  return (m.role === "user" || m.role === "assistant") && typeof m.text === "string";
};

export function loadPreauthStash(): PreauthStash | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const s = parsed as Partial<PreauthStash>;
    if (
      typeof s.sessionId !== "string" || !s.sessionId ||
      typeof s.botName !== "string" ||
      typeof s.chosenType !== "string" ||
      typeof s.userTurns !== "number" ||
      typeof s.updatedAt !== "number" ||
      !Array.isArray(s.messages) || !s.messages.every(isMessage)
    ) {
      return null;
    }
    if (Date.now() - s.updatedAt > MAX_AGE_MS) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return {
      sessionId: s.sessionId,
      botName: s.botName,
      chosenType: s.chosenType,
      messages: s.messages,
      userTurns: s.userTurns,
      suggestion: (s.suggestion ?? null) as Handoff | null,
      ...(typeof s.language === "string" ? { language: s.language } : {}),
      updatedAt: s.updatedAt,
    };
  } catch {
    return null;
  }
}

export function savePreauthStash(stash: Omit<PreauthStash, "updatedAt">): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ...stash, updatedAt: Date.now() }));
  } catch { /* quota/private-mode — the chat still works, it just won't survive a reload */ }
}

export function clearPreauthStash(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch { /* nothing to clear */ }
}
